import { useState, useMemo } from "react"
import BookCard from "./BookCard"
import BookModal from "./BookModal"

const normalize = (value) => (value || "").toString().toLowerCase()

// Ukrainian plural for "книжка": 1 → книжка, 2–4 → книжки, інше → книжок.
const booksWord = (count) => {
  const mod100 = count % 100
  const mod10 = count % 10
  if (mod100 >= 11 && mod100 <= 14) return "книжок"
  if (mod10 === 1) return "книжка"
  if (mod10 >= 2 && mod10 <= 4) return "книжки"
  return "книжок"
}

// The whole reading-journal screen. Receives books from the server (via the
// Stimulus mount) and owns the "which book is open" state — clicking an entry
// opens the modal — plus an instant client-side search over title/author/genre.
const BooksIndex = ({ books }) => {
  const [selected, setSelected] = useState(null)
  const [query, setQuery] = useState("")

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (!q) return books
    return books.filter((book) =>
      `${normalize(book.title)} ${normalize(book.author)} ${normalize(book.genre)}`.includes(q)
    )
  }, [books, q])

  return (
    <div className="journal">
      <div className="journal__content">
        <header className="journal-head">
          <h1>Читацький щоденник</h1>
          <p className="journal-head__sub">усе, що я прочитав(-ла) і хочу пам'ятати</p>
          <svg className="journal-head__rule" viewBox="0 0 220 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 7 C 40 1, 60 11, 96 6 S 160 1, 216 6" />
          </svg>
          {books.length > 0 && (
            <p className="journal-head__count">
              {q ? (
                <>Знайдено <strong>{filtered.length}</strong> із {books.length} {booksWord(books.length)}</>
              ) : (
                <><strong>{books.length}</strong> {booksWord(books.length)} у щоденнику</>
              )}
            </p>
          )}
        </header>

        {books.length > 0 && (
          <div className="journal-toolbar">
            <div className="journal-search">
              <span className="journal-search__mag" aria-hidden="true">⌕</span>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="знайти запис…"
                aria-label="Пошук книжок"
              />
              {query && (
                <button
                  type="button"
                  className="journal-search__clear"
                  onClick={() => setQuery("")}
                  aria-label="Очистити пошук"
                >
                  ×
                </button>
              )}
            </div>
            <a className="journal-add" href="/books/new">✎ додати книжку</a>
          </div>
        )}

        {books.length === 0 ? (
          <div className="journal-empty">
            <h2>Щоденник поки порожній</h2>
            <p>Додай першу прочитану книжку — і вона стане тут.</p>
            <a className="journal-add journal-empty__add" href="/books/new">✎ додати книжку</a>
          </div>
        ) : filtered.length > 0 ? (
          <section className="entries">
            {filtered.map((book) => (
              <BookCard key={book.id} book={book} onOpen={() => setSelected(book)} />
            ))}
            {!q && (
              <a className="entry entry--ghost" href="/books/new">
                <span>＋<br />новий запис</span>
              </a>
            )}
          </section>
        ) : (
          <div className="journal-empty">
            <h2>Нічого не знайшлося</h2>
            <p>За запитом «{query.trim()}» книжок немає. Спробуй інакше.</p>
          </div>
        )}
      </div>

      <BookModal book={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

export default BooksIndex
